.track {
  display: flex;
  gap: var(--space-8);
  margin: var(--space-12) calc(-1 * var(--space-16)) 0;
  padding: 2px var(--space-16) var(--space-4);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scroll-snap-type: x mandatory;
  scroll-padding-inline: var(--space-16);
  scrollbar-width: none;
}

.track::-webkit-scrollbar {
  display: none;
}

.item {
  flex: 0 0 min(232px, 78%);
  scroll-snap-align: start;
}

.track[data-single] .item {
  flex-basis: min(280px, 100%);
}

.card {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  border: 1px solid var(--w-border);
  border-radius: 16px;
  background: var(--w-bg);
  color: inherit;
  text-decoration: none;
  transition: transform 140ms var(--ease-out), border-color 140ms ease, box-shadow 200ms ease;
}

/* Pressing the card's link, not the plan button inside it. */
a.card:active,
.card:has(.stretch:active) {
  transform: scale(0.98);
}

.card:has(.stretch:focus-visible) {
  outline: 2px solid var(--w-accent-text);
  outline-offset: -2px;
}

@media (hover: hover) and (pointer: fine) {
  a.card:hover,
  .card[data-link]:hover {
    border-color: var(--w-border-strong);
    box-shadow: 0 8px 20px -12px var(--w-shadow);
  }
}

/* The title link covers the whole card (one link, not a link around buttons). */
.stretch {
  color: inherit;
  text-decoration: none;
  -webkit-touch-callout: none;
}

.stretch::after {
  position: absolute;
  inset: 0;
  content: "";
}

.stretch:focus-visible {
  outline: none;
}

.mediaWrap {
  position: relative;
}

.media {
  aspect-ratio: 16 / 10;
  background: var(--w-subtle);
}

.media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* A plan as the thumbnail: drawn on white, it must not be cropped. */
.media[data-plan] {
  background: #ffffff;
}

.media[data-plan] img {
  object-fit: contain;
}

.planButton {
  position: absolute;
  bottom: var(--space-8);
  left: var(--space-8);
  z-index: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--space-4);
  min-height: 30px;
  padding: 0 var(--space-12) 0 10px;
  border: 0;
  border-radius: 999px;
  background: rgb(255 255 255 / 0.94);
  box-shadow: 0 1px 3px rgb(16 24 40 / 0.18);
  color: #16181d;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-user-select: none;
  transition: transform 120ms var(--ease-out), background-color 120ms ease;
}

/* A 46px target around the small pill, still inside the photo. */
.planButton::before {
  position: absolute;
  inset: -8px;
  content: "";
}

.planButton :global(.icon) {
  width: 16px;
  height: 16px;
}

.planButton:active {
  transform: scale(0.96);
}

.planButton:focus-visible {
  outline: 2px solid var(--w-accent-text);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .planButton:hover {
    background: #ffffff;
  }
}

.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-12);
}

.title {
  display: -webkit-box;
  margin: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.35;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.place {
  margin: 0;
  overflow: hidden;
  color: var(--w-muted);
  font-size: 13px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.facts {
  margin: var(--space-4) 0 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.meta {
  margin: 0;
  color: var(--w-muted);
  font-size: 13px;
}

.price {
  margin: var(--space-4) 0 0;
  font-size: 15px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin: var(--space-4) 0 0;
  font-size: 12px;
  font-weight: 550;
}

.inStock {
  color: var(--w-success);
}

.outOfStock {
  color: var(--w-danger);
}

.tag {
  padding: 0 var(--space-6);
  border-radius: 6px;
  background: var(--w-subtle);
  color: var(--w-muted);
}

.actions {
  display: flex;
  gap: var(--space-6);
  margin-top: auto;
  padding-top: var(--space-12);
}

.primary,
.secondary,
.ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-6);
  min-height: 36px;
  border-radius: 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 120ms var(--ease-out), background-color 120ms ease;
}

.primary {
  flex: 1;
  padding: 0 var(--space-12);
  border: 0;
  background: var(--w-accent);
  color: var(--w-on-accent);
}

.secondary {
  width: 36px;
  border: 1px solid var(--w-border-strong);
  color: var(--w-text);
}

.ghost {
  padding: 0 var(--space-12);
  border: 1px solid var(--w-border-strong);
  background: transparent;
  color: var(--w-text);
}

.primary :global(.icon),
.secondary :global(.icon),
.ghost :global(.icon) {
  width: 16px;
  height: 16px;
}

.primary:active,
.secondary:active,
.ghost:active {
  transform: scale(0.96);
}

.primary:focus-visible,
.ghost:focus-visible {
  outline: 2px solid var(--w-accent-text);
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .primary:hover {
    background: var(--w-accent-strong);
  }

  .secondary:hover,
  .ghost:hover {
    background: var(--w-hover);
  }
}

/* ---------- floor plan viewer ---------- */
.viewer {
  width: min(calc(100% - var(--space-24)), 560px);
  max-width: none;
  max-height: calc(100% - var(--space-24));
  margin: auto;
  padding: 0;
  overflow: hidden auto;
  overscroll-behavior: contain;
  border: 0;
  border-radius: 20px;
  background: var(--w-bg);
  box-shadow: 0 24px 48px -16px rgb(0 0 0 / 0.35);
  color: var(--w-text);
  opacity: 1;
  transform: none;
  transition:
    opacity 200ms var(--ease-out),
    transform 200ms var(--ease-out),
    overlay 200ms allow-discrete,
    display 200ms allow-discrete;
}

.viewer:not([open]) {
  opacity: 0;
  transform: scale(0.96);
}

@starting-style {
  .viewer[open] {
    opacity: 0;
    transform: scale(0.96);
  }
}

.viewer::backdrop {
  background: rgb(10 12 16 / 0.55);
  opacity: 1;
  transition:
    opacity 200ms ease,
    overlay 200ms allow-discrete,
    display 200ms allow-discrete;
}

.viewer:not([open])::backdrop {
  opacity: 0;
}

@starting-style {
  .viewer[open]::backdrop {
    opacity: 0;
  }
}

.viewerHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-12);
  padding: var(--space-16) var(--space-12) var(--space-12) var(--space-16);
}

.viewerTitle {
  margin: 0;
  font-size: 16px;
  font-weight: 650;
  line-height: 1.3;
}

.viewerMeta {
  margin: var(--space-2) 0 0;
  color: var(--w-muted);
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}

.viewerClose {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin: -6px -2px 0 0;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--w-muted);
  cursor: pointer;
  touch-action: manipulation;
  transition: transform 120ms var(--ease-out), background-color 120ms ease;
}

.viewerClose :global(.icon) {
  width: 20px;
  height: 20px;
}

.viewerClose:active {
  transform: scale(0.94);
  background: var(--w-press);
}

.viewerClose:focus-visible {
  outline: 2px solid var(--w-accent-text);
  outline-offset: -2px;
}

@media (hover: hover) and (pointer: fine) {
  .viewerClose:hover {
    background: var(--w-hover);
    color: var(--w-text);
  }
}

/* Plans are drawn on white, in the dark theme too. */
.viewerImage {
  display: grid;
  min-height: 12rem;
  place-items: center;
  margin: 0 var(--space-12);
  overflow: hidden;
  border-radius: 12px;
  background: #ffffff;
}

.viewerImage img {
  display: block;
  width: 100%;
  height: auto;
  max-height: min(62vh, 520px);
  object-fit: contain;
  opacity: 0;
  transition: opacity 200ms ease;
}

.viewerImage[data-loaded] img {
  opacity: 1;
}

.viewerActions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-8);
  padding: var(--space-12);
}

.viewerActions .primary,
.viewerActions .ghost {
  min-height: 44px;
  font-size: 14px;
}

/* Reduced motion: the viewer only fades. */
@media (prefers-reduced-motion: reduce) {
  .viewer {
    transform: none !important;
  }
}
